import React, { createContext, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { getLocalStorageItem, setLocalStorageItem } from '@luxon/services';

import { lightTheme } from './light-theme';
import { darkTheme } from './dark-theme';
import { useAppSettings } from '@luxon/hooks';
import { FeatureFlag } from '@luxon/models';

export enum APP_THEME_MODE {
    Light = 'light',
    Dark = 'dark'
}

interface AppThemeContextValue {
    themeMode: APP_THEME_MODE;
    setTheme: (theme: APP_THEME_MODE) => void;
    toggleTheme: () => void;
}
export const AppThemeContext = createContext<AppThemeContextValue>({
    themeMode: APP_THEME_MODE.Light,
    setTheme: () => null,
    toggleTheme: () => null
});

interface IAppThemeProps {
    children: any;
};
export const AppTheme: FunctionComponent<IAppThemeProps> = (props: IAppThemeProps) => {

    const { isFeatureEnabled } = useAppSettings();

    const [themeMode, setThemeMode] = useState<APP_THEME_MODE>(() => {
        const themeFallback = window.matchMedia('(prefers-color-scheme: dark)').matches ? APP_THEME_MODE.Dark : APP_THEME_MODE.Light;
        return getLocalStorageItem<APP_THEME_MODE>('appTheme') || themeFallback;
    });

    const themeToUse = useMemo(() => (themeMode === APP_THEME_MODE.Dark ? darkTheme : lightTheme), [themeMode]);

    const setTheme = useCallback((mode: APP_THEME_MODE) => {
        setThemeMode(mode);
        setLocalStorageItem('appTheme', mode);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(themeMode === APP_THEME_MODE.Dark ? APP_THEME_MODE.Light : APP_THEME_MODE.Dark);
    }, [themeMode]);

    useEffect(() => {
        if (!isFeatureEnabled(FeatureFlag.DARK_MODE_TOGGLE_ENABLED)) {
            setTheme(APP_THEME_MODE.Light);
        }
    }, [isFeatureEnabled]);

    return (
        <AppThemeContext.Provider value={{ themeMode, setTheme, toggleTheme }}>
            <ThemeProvider theme={themeToUse}>
                <CssBaseline />
                {props.children}
            </ThemeProvider>
        </AppThemeContext.Provider>
    )
};